import { z } from 'zod/v4';
import { EnaApplicationStatuses } from './connectionApplication';
import { ReadableInvoiceStatus } from './invoiceTypes';
import { ReadableJobStatus } from './jobTypes';
import { scheduledEventTaskStatus } from './scheduledEventsTypes';

// ---------------------------------------------------------------------------
// Operator groups
// ---------------------------------------------------------------------------

export type InternalOperator = 'includes' | 'equals' | 'not-equals';

export type OperatorGroup =
  | 'all'
  | 'equalsOnly'
  | 'equalsAndNotEquals'
  | 'includesAndEquals';

export const operatorGroups = {
  all: ['includes', 'equals', 'not-equals'],
  equalsOnly: ['equals'],
  equalsAndNotEquals: ['equals', 'not-equals'],
  includesAndEquals: ['includes', 'equals'],
} as const satisfies Record<OperatorGroup, readonly InternalOperator[]>;

// ---------------------------------------------------------------------------
// Condition schemas — one per condition type
// ---------------------------------------------------------------------------

// INCLUDES + EQUALS + NOT_EQUALS
export const CustomerNameConditionSchema = z.object({
  field: z.literal('customer.name'),
  operator: z.enum(operatorGroups.all),
  value: z.string(),
});

export const CustomerEmailConditionSchema = z.object({
  field: z.literal('primaryContact.emailAddress'),
  operator: z.enum(operatorGroups.all),
  value: z.string(),
});

// EQUALS only
export const LocationContactsEmailConditionSchema = z.object({
  field: z.literal('event.location.contacts.emailaddress.array'),
  operator: z.enum(operatorGroups.equalsOnly),
  value: z.string(),
});

export const LocationContactsPhoneConditionSchema = z.object({
  field: z.literal('event.location.contacts.telephonenumber.array'),
  operator: z.enum(operatorGroups.equalsOnly),
  value: z.string(),
});

export const ProjectSiteAddressContactsEmailConditionSchema = z.object({
  field: z.literal('deal.siteaddresses.contacts.emailaddress.array'),
  operator: z.enum(operatorGroups.equalsOnly),
  value: z.string(),
});

export const ProjectSiteAddressContactsPhoneConditionSchema = z.object({
  field: z.literal('deal.siteaddresses.contacts.telephonenumber.array'),
  operator: z.enum(operatorGroups.equalsOnly),
  value: z.string(),
});

export const NewPipelineStageNameConditionSchema = z.object({
  field: z.literal('trigger.newPipelineStage'),
  operator: z.enum(operatorGroups.equalsOnly),
  value: z.string(),
});

export const OldPipelineStageNameConditionSchema = z.object({
  field: z.literal('trigger.oldPipelineStage'),
  operator: z.enum(operatorGroups.equalsOnly),
  value: z.string(),
});

export const ConnectionApplicationStatusConditionSchema = z.object({
  field: z.literal('connectionApplication.connectionApplicationStatus'),
  operator: z.enum(operatorGroups.equalsOnly),
  value: z.enum(EnaApplicationStatuses),
});

// EQUALS + NOT_EQUALS
export const CustomerPhoneConditionSchema = z.object({
  field: z.literal('primaryContact.telephoneNumber'),
  operator: z.enum(operatorGroups.equalsAndNotEquals),
  value: z.string(),
});

export const UserEmailConditionSchema = z.object({
  field: z.literal('user.email'),
  operator: z.enum(operatorGroups.equalsAndNotEquals),
  value: z.string(),
});

export const EventAssigneesEmailConditionSchema = z.object({
  field: z.literal('event.assignedusers.emailaddress.array'),
  operator: z.enum(operatorGroups.equalsAndNotEquals),
  value: z.string(),
});

export const AssigneesPhoneConditionSchema = z.object({
  field: z.literal('event.assignedusers.telephonenumber.array'),
  operator: z.enum(operatorGroups.equalsAndNotEquals),
  value: z.string(),
});

export const EventTaskStatusConditionSchema = z.object({
  field: z.literal('event.taskStatus'),
  operator: z.enum(operatorGroups.equalsAndNotEquals),
  value: z.enum(scheduledEventTaskStatus),
});

export const NewlyAddedTagConditionSchema = z.object({
  field: z.literal('trigger.X.tagText'),
  operator: z.enum(operatorGroups.equalsAndNotEquals),
  value: z.string(),
});

export const ProjectPipelineConditionSchema = z.object({
  field: z.literal('deal.pipelineId'),
  operator: z.enum(operatorGroups.equalsAndNotEquals),
  value: z
    .union([
      z
        .string()
        .transform((val) => (['NULL', ''].includes(val) ? null : Number(val)))
        .refine((val) => !Number.isNaN(val), {
          message: 'Invalid number string',
        }),
      z.number(),
    ])
    .nullable(),
});

export const ProjectPipelineStageNameConditionSchema = z.object({
  field: z.literal('deal.pipelineStage'),
  operator: z.enum(operatorGroups.equalsAndNotEquals),
  value: z.string(),
});

export const ProposalStatusConditionSchema = z.object({
  field: z.literal('proposal.readableStatus'),
  operator: z.enum(operatorGroups.equalsAndNotEquals),
  value: z.enum([
    ReadableJobStatus.ARCHIVED,
    ReadableJobStatus.INACTIVE,
    ReadableJobStatus.PAID,
    ReadableJobStatus.DEPOSIT_PAID,
    ReadableJobStatus.DEPOSIT_DUE,
    ReadableJobStatus.PAYMENT_PENDING,
    ReadableJobStatus.ACCEPTED,
    ReadableJobStatus.DECLINED,
    ReadableJobStatus.SENT,
    ReadableJobStatus.DRAFT,
    ReadableJobStatus.EXPIRED,
  ]),
});

export const InvoiceStatusConditionSchema = z.object({
  field: z.literal('invoice.readableStatus'),
  operator: z.enum(operatorGroups.equalsAndNotEquals),
  value: z.enum(ReadableInvoiceStatus),
});

const booleanConditionValueSchema = z.union([
  z.enum(['true', 'false']).transform((val) => val === 'true'),
  z.boolean(),
]);

export const InvoiceIsDepositConditionSchema = z.object({
  field: z.literal('invoice.isDepositInvoice'),
  operator: z.enum(operatorGroups.equalsAndNotEquals),
  value: booleanConditionValueSchema,
});

export const InvoiceIsGrantConditionSchema = z.object({
  field: z.literal('invoice.isGrantInvoice'),
  operator: z.enum(operatorGroups.equalsAndNotEquals),
  value: booleanConditionValueSchema,
});

export const TriggeredDayOfWeekConditionSchema = z.object({
  field: z.literal('trigger.triggeredAt.dayOfWeek'),
  operator: z.enum(operatorGroups.equalsAndNotEquals),
  value: z.enum([
    'Sunday',
    'Monday',
    'Tuesday',
    'Wednesday',
    'Thursday',
    'Friday',
    'Saturday',
  ]),
});

// INCLUDES + EQUALS (tag conditions)
export const EventHasTagConditionSchema = z.object({
  field: z.literal('event.tags.X.tagText'),
  operator: z.enum(operatorGroups.includesAndEquals),
  value: z.string(),
});

export const EventDoesNotHaveTagConditionSchema = z.object({
  field: z.literal('event.not.tags.X.tagText'),
  operator: z.enum(operatorGroups.includesAndEquals),
  value: z.string(),
});

export const ProjectHasTagConditionSchema = z.object({
  field: z.literal('deal.tags.X.tagText'),
  operator: z.enum(operatorGroups.includesAndEquals),
  value: z.string(),
});

export const ProjectDoesNotHaveTagConditionSchema = z.object({
  field: z.literal('deal.not.tags.X.tagText'),
  operator: z.enum(operatorGroups.includesAndEquals),
  value: z.string(),
});

// ---------------------------------------------------------------------------
// Discriminated union of all condition schemas
// ---------------------------------------------------------------------------

export const conditionSchema = z.discriminatedUnion('field', [
  CustomerNameConditionSchema,
  CustomerEmailConditionSchema,
  LocationContactsEmailConditionSchema,
  LocationContactsPhoneConditionSchema,
  ProjectSiteAddressContactsEmailConditionSchema,
  ProjectSiteAddressContactsPhoneConditionSchema,
  NewPipelineStageNameConditionSchema,
  OldPipelineStageNameConditionSchema,
  ConnectionApplicationStatusConditionSchema,
  CustomerPhoneConditionSchema,
  UserEmailConditionSchema,
  EventAssigneesEmailConditionSchema,
  AssigneesPhoneConditionSchema,
  EventTaskStatusConditionSchema,
  NewlyAddedTagConditionSchema,
  ProjectPipelineConditionSchema,
  ProjectPipelineStageNameConditionSchema,
  ProposalStatusConditionSchema,
  InvoiceStatusConditionSchema,
  InvoiceIsDepositConditionSchema,
  InvoiceIsGrantConditionSchema,
  TriggeredDayOfWeekConditionSchema,
  EventHasTagConditionSchema,
  EventDoesNotHaveTagConditionSchema,
  ProjectHasTagConditionSchema,
  ProjectDoesNotHaveTagConditionSchema,
]);

// ---------------------------------------------------------------------------
// Condition type → operator group lookup (keyed by GQL condition type name)
//
// This mirrors the schemas above. When adding a new condition schema, add its
// entry here too so frontend operator restrictions stay in sync.
// ---------------------------------------------------------------------------

export const conditionTypeOperatorGroup = {
  CustomerNameCondition: 'all',
  CustomerEmailCondition: 'all',
  LocationContactsEmailCondition: 'equalsOnly',
  LocationContactsPhoneCondition: 'equalsOnly',
  ProjectSiteAddressContactsEmailCondition: 'equalsOnly',
  ProjectSiteAddressContactsPhoneCondition: 'equalsOnly',
  NewPipelineStageNameCondition: 'equalsOnly',
  OldPipelineStageNameCondition: 'equalsOnly',
  ConnectionApplicationStatusCondition: 'equalsOnly',
  CustomerPhoneCondition: 'equalsAndNotEquals',
  UserEmailCondition: 'equalsAndNotEquals',
  EventAssigneesEmailCondition: 'equalsAndNotEquals',
  AssigneesPhoneCondition: 'equalsAndNotEquals',
  EventTaskStatusCondition: 'equalsAndNotEquals',
  NewlyAddedTagCondition: 'equalsAndNotEquals',
  ProjectPipelineCondition: 'equalsAndNotEquals',
  ProjectPipelineStageNameCondition: 'equalsAndNotEquals',
  ProposalStatusCondition: 'equalsAndNotEquals',
  InvoiceStatusCondition: 'equalsAndNotEquals',
  InvoiceIsDepositCondition: 'equalsAndNotEquals',
  InvoiceIsGrantCondition: 'equalsAndNotEquals',
  TriggeredDayOfWeekCondition: 'equalsAndNotEquals',
  EventHasTagCondition: 'includesAndEquals',
  EventDoesNotHaveTagCondition: 'includesAndEquals',
  ProjectHasTagCondition: 'includesAndEquals',
  ProjectDoesNotHaveTagCondition: 'includesAndEquals',
} as const satisfies Record<string, OperatorGroup>;

export type ConditionTypeName = keyof typeof conditionTypeOperatorGroup;

// Typed tuple of all condition type names, derived from conditionTypeOperatorGroup keys.
// Cast is unavoidable (Object.keys returns string[]) but isolated to this single location.
export const conditionTypeNames = Object.keys(conditionTypeOperatorGroup) as [
  ConditionTypeName,
  ...ConditionTypeName[],
];
